import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LandingPage } from "./components/LandingPage";
import { WorkshopPage } from "./components/WorkshopPage";
import { WorkshopRecapPage } from "./components/WorkshopRecapPage";
import { HTHFPage } from "./components/HTHFPage";
import { HTHFPartnerPage } from "./components/HTHFPartnerPage";
import { EmergentPage } from "./components/EmergentPage";
import CustomCursorEffectsPage from "./components/resources/prompts/CustomCursorEffectsPage";
import AdminDashboardResourcePage from "./components/resources/prompts/AdminDashboardPage";
import GettingStartedPage from "./components/resources/prompts/GettingStartedPage";
import ContentManagementPage from "./components/resources/prompts/ContentManagementPage";
import CloudinarySetupPage from "./components/resources/prompts/CloudinarySetupPage";
import AddOnFeaturesPage from "./components/resources/prompts/AddOnFeaturesPage";
import MorePromptsPage from "./components/resources/prompts/MorePromptsPage";
import TechStackPage from "./components/resources/stacks/TechStackPage";
import BackendSetupPage from "./components/resources/guides/BackendSetupPage";
import FrontRowPage from "./components/labs/FrontRowPage";
import Month1Workshop from "./components/labs/Month1Workshop";
import DataDreamingFrontRow from "./components/labs/DataDreamingFrontRow";
import DataDreamingMonth1 from "./components/labs/DataDreamingMonth1";
import WomensSoireeRecap from "./components/recaps/WomensSoireeRecap";
import AdminDashboard from "./components/AdminDashboard";
import AuthCallback from "./components/AuthCallback";
import MagicLink from "./components/MagicLink";
import AnalyticsTracker from "./components/AnalyticsTracker";
import EmergentBIRPage from "./components/EmergentBIRPage";
import InlineContentManagementPage from "./components/InlineContentManagementPage";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { AdminProvider, AdminToolbar } from "./components/AdminContentEditor";

// Scroll to top on route change, or to hash element if present
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      // If there's a hash, scroll to that element
      setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <AdminProvider>
            <div className="App">
              <BrowserRouter>
                <ScrollToTop />
                <AnalyticsTracker />
                <AdminToolbar />
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth/magic" element={<MagicLink />} />
                  <Route path="/emergent" element={<EmergentPage />} />
                  <Route path="/hthf-partner" element={<HTHFPartnerPage />} />
                  <Route path="/womenbusinesssoiree" element={<WorkshopPage />} />
                  <Route path="/womenbusinesssoiree/recap" element={<WorkshopRecapPage />} />
                  <Route path="/hthf" element={<HTHFPage />} />
                  {/* Resource Pages - Prompts */}
                  <Route path="/resources/prompts/custom-cursor-effects" element={<CustomCursorEffectsPage />} />
                  <Route path="/resources/prompts/admin-dashboard" element={<AdminDashboardResourcePage />} />
                  <Route path="/resources/prompts/getting-started" element={<GettingStartedPage />} />
                  <Route path="/resources/prompts/content-management" element={<ContentManagementPage />} />
                  <Route path="/resources/prompts/cloudinary-setup" element={<CloudinarySetupPage />} />
                  <Route path="/resources/prompts/add-on-features" element={<AddOnFeaturesPage />} />
                  <Route path="/resources/prompts/more-ideas" element={<MorePromptsPage />} />
                  {/* Resource Pages - Guides */}
                  <Route path="/resources/guides/backend-setup" element={<BackendSetupPage />} />
                  {/* Resource Pages - Stacks */}
                  <Route path="/resources/stacks/tech-stack" element={<TechStackPage />} />
                  <Route path="/resources" element={<Navigate to="/#resources" replace />} />
                  {/* Labs */}
                  <Route path="/lab/frontrow/phygital" element={<FrontRowPage />} />
                  <Route path="/lab/frontrow/month-1" element={<Month1Workshop />} />
                  <Route path="/lab/frontrow/datadreaming" element={<DataDreamingFrontRow />} />
                  <Route path="/lab/frontrow/datadreaming/month-1" element={<DataDreamingMonth1 />} />
                  {/* Recaps */}
                  <Route path="/recap/womens-soiree" element={<WomensSoireeRecap />} />
                  {/* Unlisted Internal Pages */}
                  <Route path="/EmergentBIR" element={<EmergentBIRPage />} />
                  <Route path="/InlineContentManagement" element={<InlineContentManagementPage />} />
                </Routes>
              </BrowserRouter>
              <Toaster 
                position="bottom-center" 
                toastOptions={{
                  style: {
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(186, 230, 253, 0.5)',
                    color: '#334155',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 10px 40px rgba(100, 150, 200, 0.15)'
                  }
                }}
              />
            </div>
          </AdminProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
